
import { useEffect, useState, useRef, RefObject } from 'react';
import * as THREE from 'three';
import { Emitter } from './waveUtils';
import { setupResizeHandler, setupMouseHandler } from './handlers/eventHandlers';
import { initializeScene, cleanupScene } from './setup/sceneSetup';
import { createAnimationLoop } from './animation/animationLoop';

interface DataWaveOptions {
  particleCount: number;
  waveWidth: number;
  waveDepth: number;
  particleSize: number;
  maxEmitters: number;
  emitterChance: number;
}

/**
 * Custom hook that creates and manages a 3D data wave visualization
 * Uses Three.js to render an animated wave of particles representing data flow
 * 
 * @param containerRef Reference to the DOM element that will contain the canvas
 * @param options Configuration options for the wave
 */
export const useDataWave = (
  containerRef: RefObject<HTMLDivElement>,
  options: DataWaveOptions
) => {
  // Track mouse position for interactive effects
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  
  // Track window size for responsive rendering
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  // Refs to store Three.js objects and animation state
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const particlesRef = useRef<THREE.Points | null>(null);
  const positionsRef = useRef<Float32Array | null>(null);
  const geometryRef = useRef<THREE.BufferGeometry | null>(null);
  const frameIdRef = useRef<number | null>(null);
  const clockRef = useRef<THREE.Clock>(new THREE.Clock());
  const emittersRef = useRef<Emitter[]>([]);

  // Initialize the Three.js scene
  useEffect(() => {
    const sceneSetup = initializeScene(containerRef, windowSize, options);
    
    if (!sceneSetup) return;
    
    // Store references to created objects
    sceneRef.current = sceneSetup.scene;
    cameraRef.current = sceneSetup.camera;
    rendererRef.current = sceneSetup.renderer;
    particlesRef.current = sceneSetup.particles;
    geometryRef.current = sceneSetup.geometry;
    positionsRef.current = sceneSetup.positions;

    // Create and start animation loop
    const animate = createAnimationLoop(
      sceneRef, 
      cameraRef, 
      rendererRef, 
      geometryRef, 
      positionsRef, 
      frameIdRef, 
      clockRef, 
      emittersRef, 
      mousePosition, 
      options
    );
    
    animate();

    // Setup event handlers
    const handleResize = setupResizeHandler(cameraRef, rendererRef, setWindowSize);
    const handleMouseMove = setupMouseHandler(setMousePosition);

    // Add event listeners
    window.addEventListener('resize', handleResize);
    window.addEventListener('mousemove', handleMouseMove);

    // Call resize handler once to set initial size
    handleResize();

    // Clean up on unmount
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('mousemove', handleMouseMove);
      cleanupScene(containerRef, rendererRef, geometryRef, particlesRef, frameIdRef);
      
      // Reset refs
      sceneRef.current = null;
      cameraRef.current = null;
      rendererRef.current = null;
      particlesRef.current = null;
      positionsRef.current = null;
      geometryRef.current = null;
      frameIdRef.current = null;
      emittersRef.current = [];
    };
  }, []);

  // Handle window resize
  useEffect(() => {
    const handleResize = setupResizeHandler(cameraRef, rendererRef, setWindowSize);
    handleResize();
  }, [windowSize.width, windowSize.height]);

  // No need to return anything from this hook
  // It manages the canvas internally through the containerRef
};
